import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useDownloads, useTerminals, type PlatformView, type ReleaseView, type TerminalView } from "../../hooks/hardware";
import { useLocale } from "../../hooks/useLocale";
import { apiBase, terminalId } from "../../lib/config";
import { api, ApiError, getBranch } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";

const fmtSize = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const VERSION = /^\d{1,4}\.\d{1,4}\.\d{1,4}(?:-[0-9A-Za-z.]{1,20})?$/;

function Release({ r, latest }: { r: ReleaseView; latest?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      // a 5-minute signed link: the browser then streams the file straight from the server (no big blob in memory, resumable)
      const { url } = await api.post<{ url: string }>(`/downloads/${r.platform}/${r.version}/link`);
      const a = document.createElement("a");
      a.href = `${apiBase()}${url.replace(/^\/api\/v2/, "")}`;
      a.download = r.file;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast(t("dl.started", { file: r.file }), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }
  const notes = (locale === "ar" && r.notesAr) || r.notes;
  return (
    <div className={`rounded-2xl border p-3 ${latest ? "border-cosmic-purple bg-purple-50/50" : "border-gray-200 bg-gray-50"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-black">{t("dl.version")} <span className="num font-mono">{r.version}</span> {latest && <Pill tone="purple">{t("dl.latest")}</Pill>}</div>
          <div className="text-[11px] font-semibold text-gray-500"><span className="num">{fmtSize(r.size)}</span> · <span className="num">{formatDateTime(r.publishedAt, locale)}</span></div>
        </div>
        <button type="button" className={latest ? "btn-primary" : "btn-ghost"} disabled={busy} onClick={() => void download()}>⬇️ {t("dl.download")}</button>
      </div>
      {notes && <p className="mt-2 text-xs text-gray-600">{notes}</p>}
      <details className="mt-2 text-[10px] font-semibold text-gray-500">
        <summary className="cursor-pointer">{t("dl.checksum")}</summary>
        <code className="num mt-1 block break-all font-mono">{r.sha256}</code>
        <span>{t("dl.checksumHelp")}</span>
      </details>
    </div>
  );
}

function PublishDialog({ platform, onClose }: { platform: "windows" | "android"; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [notesAr, setNotesAr] = useState("");
  const [progress, setProgress] = useState<number | null>(null);

  function upload() {
    const f = file.current?.files?.[0];
    if (!f) return;
    const token = (() => { try { return localStorage.getItem("astro.token"); } catch { return null; } })();
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${apiBase()}/downloads/${platform}/${version}?notes=${encodeURIComponent(notes)}&notesAr=${encodeURIComponent(notesAr)}`);
    xhr.setRequestHeader("content-type", "application/octet-stream");
    xhr.setRequestHeader("x-terminal-id", terminalId());
    if (token) xhr.setRequestHeader("authorization", `Bearer ${token}`);
    const b = getBranch();
    if (b) xhr.setRequestHeader("x-branch-id", b);
    xhr.upload.onprogress = (e) => e.lengthComputable && setProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      setProgress(null);
      if (xhr.status === 201) {
        toast(t("dl.published"), "success");
        void qc.invalidateQueries({ queryKey: ["hw", "downloads"] });
        onClose();
      } else {
        const j = (() => { try { return JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string } }; } catch { return {}; } })() as { error?: { code?: string; message?: string } };
        toastError(new ApiError(xhr.status, j.error?.code ?? "HTTP_ERROR", j.error?.message ?? xhr.statusText));
      }
    };
    xhr.onerror = () => { setProgress(null); toastError(new ApiError(0, "NETWORK_ERROR", "Cannot reach the server")); };
    setProgress(0);
    xhr.send(f);
  }
  const ext = platform === "windows" ? ".exe" : ".apk";
  return (
    <Modal open title={t("dl.publishTitle", { platform: t(`dl.platform.${platform}`) })} onClose={onClose} size="sm"
      footer={<><button type="button" className="btn-ghost" onClick={onClose} disabled={progress !== null}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={progress !== null || !VERSION.test(version)} onClick={upload}>{progress === null ? t("dl.publish") : `${progress}%`}</button></>}>
      <label className="block"><span className="label">{t("dl.installerFile", { ext })}</span><input ref={file} type="file" accept={ext} className="field" /></label>
      <label className="block"><span className="label">{t("dl.version")}</span><input className="field num" placeholder="1.2.0" value={version} onChange={(e) => setVersion(e.target.value.trim())} /></label>
      <label className="block"><span className="label">{t("dl.notesEn")}</span><textarea className="field min-h-14" value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      <label className="block"><span className="label">{t("dl.notesAr")}</span><textarea className="field min-h-14" dir="rtl" value={notesAr} onChange={(e) => setNotesAr(e.target.value)} /></label>
      <p className="text-[10px] font-semibold text-gray-500">{t("dl.publishHelp")}</p>
    </Modal>
  );
}

function Windows({ p }: { p: PlatformView }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [publish, setPublish] = useState(false);
  return (
    <section className="card space-y-3 p-5" aria-label={t("dl.platform.windows")}>
      <SectionHeader icon="🪟" title={t("dl.windows.title")} subtitle={t("dl.windows.subtitle")} right={user?.role === "owner" ? <button type="button" className="btn-ghost" onClick={() => setPublish(true)}>⬆️ {t("dl.publishNew")}</button> : undefined} />
      {p.latest ? (
        <>
          <Release r={p.latest} latest />
          {p.history.length > 0 && <details className="text-xs"><summary className="cursor-pointer font-bold text-gray-500">{t("dl.older", { count: p.history.length })}</summary><div className="mt-2 space-y-2">{p.history.map((h) => <Release key={h.version} r={h} />)}</div></details>}
        </>
      ) : <Empty>{t("dl.windows.notPublished")}</Empty>}
      <div className="rounded-2xl bg-gray-50 p-3 text-[11px] font-semibold leading-relaxed text-gray-600">
        <b className="text-cosmic-navy">{t("dl.install.title")}</b>
        <ol className="ms-4 mt-1 list-decimal space-y-0.5"><li>{t("dl.install.s1")}</li><li>{t("dl.install.s2")}</li><li>{t("dl.install.s3")}</li><li>{t("dl.install.s4")}</li></ol>
        <p className="mt-2 text-amber-700">{t("dl.install.unsigned")}</p>
      </div>
      {publish && <PublishDialog platform="windows" onClose={() => setPublish(false)} />}
    </section>
  );
}

function Android({ p }: { p: PlatformView }) {
  const { t } = useTranslation();
  return (
    <section className="card space-y-3 p-5" aria-label={t("dl.platform.android")}>
      <SectionHeader icon="🤖" title={t("dl.android.title")} subtitle={t("dl.android.subtitle")} right={p.status === "coming_soon" ? <Pill tone="amber">{t("dl.comingSoon")}</Pill> : undefined} />
      {p.latest ? <Release r={p.latest} latest /> : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center">
          <div className="text-3xl" aria-hidden>📱</div>
          <div className="mt-1 text-sm font-black">{t("dl.comingSoon")}</div>
          <p className="mt-1 text-xs text-gray-500">{t("dl.android.planned")}</p>
          <button type="button" className="btn-ghost mt-3" disabled>{t("dl.android.button")}</button>
        </div>
      )}
    </section>
  );
}

const printerTone = (s: TerminalView["status"]) => (s?.printer?.state === "error" ? "red" : s?.printer?.state === "ok" || s?.printer?.state === "idle" ? "green" : "gray");

function Terminals() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isAdmin } = useAuth();
  const { toastError } = useToast();
  const qc = useQueryClient();
  const q = useTerminals();
  const list = q.data?.terminals ?? [];
  async function toggle(x: TerminalView) {
    try { await api.patch(`/terminals/${x.id}`, { active: !x.active }); await qc.invalidateQueries({ queryKey: ["hw", "terminals"] }); } catch (e) { toastError(e); }
  }
  return (
    <section className="card space-y-3 p-5" aria-label={t("dl.terminals.title")}>
      <SectionHeader icon="🖥️" title={t("dl.terminals.title")} subtitle={t("dl.terminals.subtitle")} right={q.data?.latestVersion ? <Pill tone="purple">{t("dl.terminals.latest", { version: q.data.latestVersion })}</Pill> : undefined} />
      {list.length === 0 ? <Empty>{t("dl.terminals.none")}</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-separate border-spacing-0 text-xs">
            <thead><tr>{[t("dl.terminals.terminal"), t("dl.terminals.app"), t("dl.terminals.printer"), t("dl.terminals.lastSeen"), ""].map((h, i) => <th key={i} className="border-b border-gray-200 px-2 py-2 text-start text-[10px] font-black uppercase tracking-wide text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="[&_td]:border-b [&_td]:border-gray-100 [&_td]:px-2 [&_td]:py-2 [&_td]:align-middle">
              {list.map((x) => (
                <tr key={x.id} className={x.active ? "" : "opacity-50"}>
                  <td><span className={`me-1.5 inline-block h-2 w-2 rounded-full ${x.online ? "bg-emerald-500" : "bg-gray-300"}`} aria-label={x.online ? t("dl.terminals.online") : t("dl.terminals.offline")} /><b className="num">{x.name}</b> <span className="num font-mono text-[10px] text-gray-400">{x.branchCode}</span>{x.hasOverride && <span className="ms-1" title={t("dl.terminals.override")}>🧾</span>}</td>
                  <td>{x.status?.client ? <><span className="font-bold">{t(`dl.client.${x.status.client}`, { defaultValue: x.status.client })}</span> <span className="num font-mono">{x.status.appVersion ?? ""}</span>{x.outdated && <Pill tone="amber">{t("dl.terminals.outdated")}</Pill>}</> : <span className="text-gray-400">—</span>}</td>
                  <td>{x.status?.printer?.state ? <><Pill tone={printerTone(x.status)}>{t(`hw.state.${x.status.printer.state}`)}</Pill>{!!x.status.printer.failed && <span className="ms-1 font-black text-red-600">⚠️ {x.status.printer.failed}</span>}{x.status.printer.lastError && <div className="max-w-[16rem] truncate text-[10px] font-semibold text-red-600" title={x.status.printer.lastError}>{x.status.printer.lastError}</div>}</> : <span className="text-gray-400">—</span>}</td>
                  <td className="num">{x.lastSeenAt ? formatDateTime(x.lastSeenAt, locale) : "—"}</td>
                  <td className="text-end">{isAdmin && <button type="button" className="btn-ghost !px-2 !py-0.5 text-[10px]" onClick={() => void toggle(x)}>{x.active ? t("dl.terminals.disable") : t("dl.terminals.enable")}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Downloads & Terminals: installers for the Windows POS and (soon) the Android app, plus the live state of every terminal. */
export function DownloadsPanel() {
  const { t } = useTranslation();
  const q = useDownloads();
  const platforms = q.data ?? [];
  const win = platforms.find((p) => p.platform === "windows");
  const and = platforms.find((p) => p.platform === "android");
  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>⬇️</div>
        <div><h1 className="text-xl font-black tracking-tight">{t("dl.title")}</h1><p className="text-xs font-medium text-gray-500">{t("dl.subtitle")}</p></div>
      </div>
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        {win ? <Windows p={win} /> : <div className="card h-48 animate-pulse bg-gray-100" />}
        {and ? <Android p={and} /> : <div className="card h-48 animate-pulse bg-gray-100" />}
      </div>
      <Terminals />
    </div>
  );
}
